import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRegistrations } from '../context/RegistrationsContext';
import { CATEGORIES, CAT_STYLE } from '../categories';

function OwnerProfile({ token, onLogout, onProfileUpdate, onTokenRefresh }) {
  const navigate = useNavigate();
  const { regsByDriver } = useRegistrations();

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    owner_name: '', owner_photo_url: '', is_racing: false,
    owner_dob: '', owner_phone: '', owner_emergency_phone: '', owner_address: '',
    owner_license_number: '', owner_uci_id: '', owner_category: 'Elite', owner_points: 0,
  });
  const [allEvents, setAllEvents] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [formSnapshot, setFormSnapshot] = useState(null); // for cancel

  // Transfer state
  const [drivers, setDrivers] = useState([]);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferSending, setTransferSending] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  // Password change
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);

  // Team delete
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dangerOpen, setDangerOpen] = useState(false);

  const fileRef = useRef();
  const h = { Authorization: `Bearer ${token}` };

  const flash = (text, type = 'ok') => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  useEffect(() => {
    axios.get('/api/owner/profile', { headers: h }).then(res => {
      const d = res.data;
      setProfile(d);
      const f = {
        owner_name: d.owner_name || d.name || '',
        owner_photo_url: d.owner_photo_url || '',
        is_racing: !!d.is_racing,
        owner_dob: d.owner_dob ? d.owner_dob.slice(0, 10) : '',
        owner_phone: d.owner_phone || '',
        owner_emergency_phone: d.owner_emergency_phone || '',
        owner_address: d.owner_address || '',
        owner_license_number: d.owner_license_number || '',
        owner_uci_id: d.owner_uci_id || '',
        owner_category: d.owner_category || 'Elite',
        owner_points: d.owner_points ?? 0,
      };
      setForm(f);
      setFormSnapshot(f);
      if (d.owner_photo_url) setPhotoPreview(d.owner_photo_url);
    }).catch(() => {});

    axios.get('/api/drivers', { headers: h }).then(res => {
      // Exclude own owner row from transfer targets
      setDrivers((res.data || []).filter(d => !d._isOwner));
    }).catch(() => {});

    axios.get('/api/events', { headers: h }).then(res => {
      setAllEvents(res.data || []);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Registered events (from context, stays in sync)
  const ownerDriverId = profile?.owner_driver_id;
  const ownerEvents = useMemo(() => {
    if (!ownerDriverId || !form.is_racing) return [];
    const myRegs = (regsByDriver[ownerDriverId] || []).filter(r => r.status === 'participating' || r.status === 'assigned');
    const myEventIds = new Set(myRegs.map(r => r.event_id));
    return allEvents.filter(e => myEventIds.has(e.id)).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [regsByDriver, allEvents, ownerDriverId, form.is_racing]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const MAX = 400;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
      URL.revokeObjectURL(url);
      setPhotoPreview(dataUrl);
      set('owner_photo_url', dataUrl);
    };
    img.src = url;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put('/api/owner/profile', form, { headers: h });
      flash('Profil gespeichert ✓');
      setFormSnapshot(form);
      setEditing(false);
      if (onProfileUpdate) {
        onProfileUpdate({
          is_racing: !!res.data.is_racing,
          ownerDriverId: res.data.owner_driver_id || null,
        });
      }
      setProfile(res.data);
    } catch (err) {
      flash(err.response?.data?.error || 'Fehler beim Speichern', 'err');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (formSnapshot) {
      setForm(formSnapshot);
      setPhotoPreview(formSnapshot.owner_photo_url || profile?.owner_photo_url || null);
    }
    setEditing(false);
  };

  const handleTransfer = async () => {
    if (!transferTarget) return;
    const target = drivers.find(d => d.id === transferTarget);
    if (!window.confirm(`Ownership wirklich an ${target?.name} übertragen? Diese Anfrage muss vom Fahrer angenommen werden.`)) return;
    setTransferSending(true);
    try {
      await axios.post('/api/owner/transfer-request', { to_driver_id: transferTarget }, { headers: h });
      flash('Transfer-Anfrage gesendet ✓');
      setTransferTarget(null);
      setTransferOpen(false);
    } catch (err) {
      flash(err.response?.data?.error || err.message, 'err');
    } finally {
      setTransferSending(false);
    }
  };

  const handlePasswordChange = async () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) return flash('Passwörter stimmen nicht überein', 'err');
    setPwSaving(true);
    try {
      const res = await axios.put('/api/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword }, { headers: h });
      if (res.data.token && onTokenRefresh) onTokenRefresh(res.data.token);
      flash('Passwort geändert ✓');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      flash(err.response?.data?.error || 'Fehler', 'err');
    } finally {
      setPwSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (deleteConfirm !== 'LÖSCHEN') return flash('Bitte "LÖSCHEN" eintippen zur Bestätigung', 'err');
    if (!deletePassword) return flash('Passwort erforderlich', 'err');
    setDeleting(true);
    try {
      await axios.delete('/api/owner/team', { headers: h, data: { password: deletePassword } });
      onLogout();
    } catch (err) {
      flash(err.response?.data?.error || 'Fehler', 'err');
      setDeleting(false);
    }
  };

  const initials = (form.owner_name || profile?.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const inputStyle = {
    width: '100%', boxSizing: 'border-box', background: 'transparent', border: 'none',
    borderBottom: '1px solid rgba(59,73,75,0.6)', color: '#e2e2e8',
    fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none',
    transition: 'border-color 0.2s',
  };
  const inputDisabledStyle = {
    ...inputStyle,
    color: 'var(--outline)',
    opacity: 0.5,
    cursor: 'not-allowed',
  };
  const labelStyle = {
    display: 'block', marginBottom: 7, fontFamily: "'JetBrains Mono', monospace",
    fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#849495',
  };
  const fmtDate = (str) => {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
  };

  return (
    <div>
      <style>{`
        .op-input:focus { border-bottom-color: var(--brand-primary) !important; }
        .op-accordion-header { cursor: pointer; user-select: none; }
        .op-accordion-header:hover { background: rgba(59,73,75,0.08); }
      `}</style>

      {/* Flash */}
      {msg && (
        <div style={{ position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)', zIndex: 999, padding: '10px 20px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', whiteSpace: 'nowrap', backdropFilter: 'blur(12px)', background: msg.type === 'err' ? 'rgba(147,0,10,0.15)' : 'rgba(var(--brand-primary-rgb),0.1)', border: `1px solid ${msg.type === 'err' ? 'rgba(255,180,171,0.3)' : 'rgba(var(--brand-primary-rgb),0.3)'}`, color: msg.type === 'err' ? '#ffb4ab' : 'var(--brand-primary)' }}>
          {msg.text}
        </div>
      )}

      {/* Page Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 4 }}>TEAM OWNER</div>
          <h1 style={{ margin: 0 }}>MEIN PROFIL</h1>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{ padding: '10px 20px', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', color: 'var(--brand-primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
          >
            ✎ BEARBEITEN
          </button>
        )}
      </div>

      {/* ── 2-Column Layout ── */}
      <form onSubmit={handleSave}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', alignItems: 'start' }}>

          {/* ════════ LEFT COLUMN ════════ */}
          <div className="glass-panel" style={{ padding: '2rem' }}>

            {/* Avatar + name header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.25)' }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div
                  onClick={() => editing && fileRef.current?.click()}
                  style={{ width: 80, height: 80, borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', background: 'rgba(var(--brand-primary-rgb),0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: editing ? 'pointer' : 'default', position: 'relative' }}
                >
                  {photoPreview
                    ? <img src={photoPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, color: 'var(--brand-primary)' }}>{initials}</span>
                  }
                  {editing && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = 1}
                      onMouseLeave={e => e.currentTarget.style.opacity = 0}
                    ><span style={{ fontSize: 20 }}>📷</span></div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
              </div>
              <div>
                {editing && (
                  <button type="button" onClick={() => fileRef.current?.click()} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 8, opacity: 0.75 }}>
                    📷 FOTO ÄNDERN
                  </button>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 8px', background: 'rgba(var(--brand-primary-rgb),0.15)', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', color: 'var(--brand-primary)' }}>TEAM OWNER</span>
                  {form.is_racing && (
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', padding: '2px 8px', background: 'rgba(var(--brand-secondary-rgb),0.12)', border: '1px solid rgba(var(--brand-secondary-rgb),0.35)', color: 'var(--brand-secondary)' }}>RACING</span>
                  )}
                </div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: '1.15rem', fontWeight: 800, fontStyle: 'italic', color: 'var(--on-surface)' }}>{form.owner_name || profile?.name || '—'}</div>
                {profile?.public_id && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--outline)', marginTop: 3, letterSpacing: '0.06em' }}>{profile.public_id}</div>}
              </div>
            </div>

            {/* ── PERSÖNLICHE DATEN ── */}
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: '1rem' }}>PERSÖNLICHE DATEN</div>

            {[
              { label: 'Name', key: 'owner_name', type: 'text', placeholder: 'Vor- und Nachname' },
              { label: 'Geburtsdatum', key: 'owner_dob', type: 'date', placeholder: '' },
              { label: 'E-Mail', key: '_email', type: 'email', placeholder: '', readOnly: true },
              { label: 'Telefon', key: 'owner_phone', type: 'tel', placeholder: '+49 ...' },
              { label: 'Notfallnummer', key: 'owner_emergency_phone', type: 'tel', placeholder: '+49 ...' },
              { label: 'Adresse', key: 'owner_address', type: 'text', placeholder: 'Straße, PLZ Ort' },
            ].map(({ label, key, type, placeholder, readOnly }) => {
              const isDisabled = readOnly || !editing;
              return (
                <div key={key} style={{ marginBottom: 18 }}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    type={type}
                    value={key === '_email' ? (profile?.email || '') : (form[key] ?? '')}
                    placeholder={placeholder}
                    readOnly={isDisabled}
                    onChange={isDisabled ? undefined : e => set(key, e.target.value)}
                    className="op-input"
                    style={{ ...(isDisabled ? inputDisabledStyle : inputStyle) }}
                    onFocus={isDisabled ? undefined : e => e.target.style.borderBottomColor = 'var(--brand-primary)'}
                    onBlur={isDisabled ? undefined : e => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)'}
                  />
                </div>
              );
            })}

            {/* ── IS RACING TOGGLE ── */}
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(59,73,75,0.2)', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--on-surface)', marginBottom: 3 }}>ICH FAHRE RENNEN</div>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: '#849495' }}>Eigene Anmeldungen, Fahrerliste, Erinnerungen</div>
                </div>
                <div
                  onClick={() => editing && set('is_racing', !form.is_racing)}
                  style={{ width: 44, height: 24, borderRadius: 12, background: form.is_racing ? 'var(--brand-primary)' : 'rgba(59,73,75,0.5)', cursor: editing ? 'pointer' : 'not-allowed', position: 'relative', transition: 'background 0.2s', flexShrink: 0, opacity: editing ? 1 : 0.7 }}
                >
                  <div style={{ position: 'absolute', top: 2, left: form.is_racing ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
                </div>
              </div>
            </div>

            {/* ── RENNDATEN ── */}
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: '1rem' }}>RENNDATEN</div>

            {/* Fahrer-ID — read-only */}
            <div style={{ marginBottom: 18 }}>
              <label style={labelStyle}>FAHRER-ID</label>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700, color: form.is_racing && profile?.owner_driver_id ? 'var(--primary)' : 'var(--outline)', letterSpacing: '0.06em', opacity: form.is_racing ? 1 : 0.4, padding: '6px 0', borderBottom: '1px solid rgba(59,73,75,0.3)' }}>
                {profile?.owner_driver_id ? `DR-${String(profile.owner_driver_id).padStart(4, '0')}` : '—'}
              </div>
            </div>

            {[
              { label: 'Lizenznummer', key: 'owner_license_number', placeholder: 'z.B. 12345678' },
              { label: 'UCI-ID', key: 'owner_uci_id', placeholder: 'GER19920101' },
            ].map(({ label, key, placeholder }) => {
              const canEdit = editing && form.is_racing;
              return (
                <div key={key} style={{ marginBottom: 18 }}>
                  <label style={{ ...labelStyle, opacity: form.is_racing ? 1 : 0.5 }}>{label}</label>
                  <input
                    type="text"
                    value={form[key] ?? ''}
                    placeholder={placeholder}
                    disabled={!canEdit}
                    onChange={canEdit ? e => set(key, e.target.value) : undefined}
                    className="op-input"
                    style={{ ...(canEdit ? inputStyle : inputDisabledStyle) }}
                    onFocus={canEdit ? e => e.target.style.borderBottomColor = 'var(--brand-primary)' : undefined}
                    onBlur={canEdit ? e => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)' : undefined}
                  />
                </div>
              );
            })}

            {/* Rennklasse */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ ...labelStyle, opacity: form.is_racing ? 1 : 0.5 }}>RENNKLASSE</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {CATEGORIES.map(c => {
                  const active = form.owner_category === c;
                  const cs = CAT_STYLE[c] || CAT_STYLE.Elite;
                  const canEdit = editing && form.is_racing;
                  return (
                    <button
                      key={c}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => canEdit && set('owner_category', c)}
                      style={{ padding: '6px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', border: `1px solid ${active && form.is_racing ? cs.color : 'rgba(59,73,75,0.4)'}`, background: active && form.is_racing ? `${cs.color}18` : 'transparent', color: active && form.is_racing ? cs.color : '#849495', cursor: canEdit ? 'pointer' : 'not-allowed', opacity: form.is_racing ? (canEdit ? 1 : 0.7) : 0.5, transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                      {c.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Punkte */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ ...labelStyle, opacity: form.is_racing ? 1 : 0.5 }}>PUNKTE</label>
              <input
                type="number" min="0"
                value={form.owner_points}
                disabled={!(editing && form.is_racing)}
                onChange={(editing && form.is_racing) ? e => set('owner_points', parseInt(e.target.value) || 0) : undefined}
                style={{ ...((editing && form.is_racing) ? inputStyle : inputDisabledStyle) }}
                onFocus={(editing && form.is_racing) ? e => e.target.style.borderBottomColor = 'var(--brand-primary)' : undefined}
                onBlur={(editing && form.is_racing) ? e => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)' : undefined}
              />
            </div>

            {/* ── PASSWORT ÄNDERN ── */}
            {editing && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(59,73,75,0.2)' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: '1rem' }}>PASSWORT ÄNDERN</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[['currentPassword', 'Aktuelles Passwort'], ['newPassword', 'Neues Passwort'], ['confirmPassword', 'Neues Passwort (Wdh.)']].map(([key, label]) => (
                    <div key={key}>
                      <label style={labelStyle}>{label}</label>
                      <input
                        type="password"
                        value={pwForm[key]}
                        onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                        style={inputStyle}
                        onFocus={e => e.target.style.borderBottomColor = 'var(--brand-primary)'}
                        onBlur={e => e.target.style.borderBottomColor = 'rgba(59,73,75,0.6)'}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={pwSaving}
                    style={{ alignSelf: 'flex-start', padding: '9px 18px', background: 'transparent', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', color: 'var(--brand-primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: pwSaving ? 'not-allowed' : 'pointer', opacity: pwSaving ? 0.6 : 1 }}
                  >
                    {pwSaving ? '...' : 'PASSWORT ÄNDERN'}
                  </button>
                </div>
              </div>
            )}

            {/* Save / Cancel buttons */}
            {editing && (
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(59,73,75,0.2)', display: 'flex', gap: 10 }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: '12px 28px', background: 'var(--brand-primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, boxShadow: '0 0 16px rgba(var(--brand-primary-rgb),0.25)' }}
                >
                  {saving ? 'SPEICHERT...' : 'SPEICHERN'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  style={{ padding: '12px 20px', background: 'transparent', border: '1px solid rgba(59,73,75,0.4)', color: 'var(--outline)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', opacity: saving ? 0.5 : 1 }}
                >
                  ABBRECHEN
                </button>
              </div>
            )}
          </div>

          {/* ════════ RIGHT COLUMN ════════ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* ── Angemeldete Rennen ── */}
            <div className="glass-panel" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)' }}>ANGEMELDETE RENNEN</div>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: form.is_racing ? 'var(--primary)' : 'var(--outline)', opacity: form.is_racing ? 1 : 0.35, lineHeight: 1 }}>
                  {ownerEvents.length}
                </span>
              </div>

              {!form.is_racing ? (
                <div style={{ padding: '1rem', background: 'rgba(59,73,75,0.1)', border: '1px solid rgba(59,73,75,0.2)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', letterSpacing: '0.08em', textAlign: 'center' }}>
                  NICHT AKTIV — RACING DEAKTIVIERT
                </div>
              ) : ownerEvents.length === 0 ? (
                <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'var(--outline)', fontStyle: 'italic', paddingTop: 4 }}>Noch bei keinem Event angemeldet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ownerEvents.map(ev => (
                    <div key={ev.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(var(--brand-primary-rgb),0.12)', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 700, color: 'var(--on-surface)', fontStyle: 'italic', letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.name}</div>
                        {ev.location && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', marginTop: 2, letterSpacing: '0.06em' }}>📍 {ev.location}</div>}
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.08em', flexShrink: 0 }}>
                        {fmtDate(ev.date)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── OWNERSHIP ÜBERTRAGEN — Dropdown/Accordion ── */}
            <div className="glass-panel" style={{ overflow: 'hidden' }}>
              <div
                className="op-accordion-header"
                onClick={() => setTransferOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: transferOpen ? '1px solid rgba(59,73,75,0.3)' : 'none' }}
              >
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 2 }}>OWNERSHIP</div>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'var(--on-surface-variant)' }}>Team-Ownership übertragen</div>
                </div>
                <span style={{ color: 'var(--primary)', fontSize: 18, transition: 'transform 0.25s', transform: transferOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>⌄</span>
              </div>

              {transferOpen && (
                <div style={{ padding: '1.25rem 1.5rem' }}>
                  <p style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#849495', lineHeight: 1.6, marginBottom: '1rem', marginTop: 0 }}>
                    Wähle einen Fahrer. Er erhält eine Anfrage und kann sie in seinen Notifications annehmen. Nach Annahme wird er neuer Owner — du bleibst als Team Manager.
                  </p>
                  {drivers.filter(d => !d._isOwner).length === 0 ? (
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', marginBottom: '1rem' }}>Keine Fahrer im Team</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '1rem' }}>
                      {drivers.filter(d => !d._isOwner).map(d => (
                        <div
                          key={d.id}
                          onClick={() => setTransferTarget(d.id === transferTarget ? null : d.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer', border: `1px solid ${transferTarget === d.id ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.3)'}`, background: transferTarget === d.id ? 'rgba(var(--brand-primary-rgb),0.06)' : 'transparent', transition: 'all 0.15s' }}
                        >
                          <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', background: '#1e2124', border: '1px solid rgba(59,73,75,0.4)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {d.photo_url
                              ? <img src={d.photo_url} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <span style={{ fontFamily: "var(--font-display)", fontSize: 10, fontWeight: 800, color: 'var(--brand-primary)' }}>{(d.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}</span>
                            }
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#e2e2e8', fontWeight: 500 }}>{d.name}</div>
                            {d.is_co_admin && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, color: 'var(--brand-secondary)', letterSpacing: '0.1em' }}>TEAM MANAGER</span>}
                          </div>
                          {transferTarget === d.id && <span style={{ color: 'var(--brand-primary)', fontSize: 16 }}>✓</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleTransfer}
                    disabled={!transferTarget || transferSending}
                    style={{ padding: '10px 20px', background: transferTarget ? 'rgba(var(--brand-primary-rgb),0.12)' : 'rgba(59,73,75,0.2)', border: `1px solid ${transferTarget ? 'rgba(var(--brand-primary-rgb),0.4)' : 'rgba(59,73,75,0.3)'}`, color: transferTarget ? 'var(--brand-primary)' : '#849495', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: transferTarget ? 'pointer' : 'not-allowed', opacity: transferSending ? 0.6 : 1 }}
                  >
                    {transferSending ? 'WIRD GESENDET...' : 'ANFRAGE SENDEN'}
                  </button>
                </div>
              )}
            </div>

            {/* ── GEFAHRENZONE — Dropdown/Accordion ── */}
            <div style={{ background: 'rgba(147,0,10,0.05)', border: '1px solid rgba(255,180,171,0.2)', overflow: 'hidden' }}>
              <div
                className="op-accordion-header"
                onClick={() => setDangerOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: dangerOpen ? '1px solid rgba(255,180,171,0.2)' : 'none' }}
              >
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: '#ffb4ab', marginBottom: 2 }}>GEFAHRENZONE</div>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'rgba(255,180,171,0.6)' }}>Team dauerhaft löschen</div>
                </div>
                <span style={{ color: '#ffb4ab', fontSize: 18, transition: 'transform 0.25s', transform: dangerOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>⌄</span>
              </div>

              {dangerOpen && (
                <div style={{ padding: '1.25rem 1.5rem' }}>
                  <p style={{ fontFamily: "var(--font-display)", fontSize: 13, color: '#849495', lineHeight: 1.6, marginTop: 0, marginBottom: '1.25rem' }}>
                    Das Team und alle Daten (Events, Anmeldungen, Benachrichtigungen) werden dauerhaft gelöscht. Fahrer-Accounts bleiben erhalten. Nicht rückgängig machbar.
                  </p>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', marginBottom: 7, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#ffb4ab' }}>PASSWORT BESTÄTIGEN</label>
                    <input
                      type="password"
                      value={deletePassword}
                      onChange={e => setDeletePassword(e.target.value)}
                      style={{ ...inputStyle, borderBottomColor: 'rgba(255,180,171,0.4)' }}
                      onFocus={e => e.target.style.borderBottomColor = '#ffb4ab'}
                      onBlur={e => e.target.style.borderBottomColor = 'rgba(255,180,171,0.4)'}
                    />
                  </div>
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', marginBottom: 7, fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#ffb4ab' }}>ZUR BESTÄTIGUNG "LÖSCHEN" EINTIPPEN</label>
                    <input
                      type="text"
                      value={deleteConfirm}
                      onChange={e => setDeleteConfirm(e.target.value)}
                      placeholder="LÖSCHEN"
                      style={{ ...inputStyle, borderBottomColor: 'rgba(255,180,171,0.4)' }}
                      onFocus={e => e.target.style.borderBottomColor = '#ffb4ab'}
                      onBlur={e => e.target.style.borderBottomColor = 'rgba(255,180,171,0.4)'}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleDeleteTeam}
                    disabled={deleting || deleteConfirm !== 'LÖSCHEN' || !deletePassword}
                    style={{ padding: '10px 20px', background: 'rgba(255,180,171,0.12)', border: '1px solid rgba(255,180,171,0.4)', color: '#ffb4ab', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', opacity: (deleting || deleteConfirm !== 'LÖSCHEN' || !deletePassword) ? 0.4 : 1 }}
                  >
                    {deleting ? 'WIRD GELÖSCHT...' : '⚠ TEAM ENDGÜLTIG LÖSCHEN'}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </form>

      {/* ── MOBILE: Einstellungen + Logout ── */}
      <div className="dp-mobile-actions">
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', marginBottom: 12 }}>KONTO</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            onClick={() => navigate('/settings')}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(59,73,75,0.35)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <span style={{ fontSize: 16 }}>⚙</span> EINSTELLUNGEN
          </button>
          <button
            type="button"
            onClick={onLogout}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(59,73,75,0.35)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', textAlign: 'left', width: '100%' }}
          >
            <span style={{ fontSize: 16 }}>⏻</span> ABMELDEN
          </button>
        </div>
      </div>
    </div>
  );
}

export default OwnerProfile;
